import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { DropdownGroup } from "../../models/dropdown-group";
import { DropdownValue } from "../../models/dropdown-value";

@Component({
  selector: "dua-dropdown",
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: "./dropdown.component.html",
  styleUrls: ["./dropdown.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true,
    },
  ],
})
export class DropdownComponent implements ControlValueAccessor, OnDestroy {
  @Input() options: DropdownValue[] | null = null;
  @Input() groups: DropdownGroup[] | null = null;
  @Input() placeholder = "";
  @Input() disabled = false;

  @HostBinding("class.dropdown") readonly dropdownClass = true;
  @HostBinding("class.small") @Input() small = false;

  @ViewChild("trigger") trigger?: ElementRef<HTMLButtonElement>;

  open = false;
  value: unknown = null;

  panelTop: number | null = null;
  panelBottom: number | null = null;
  panelLeft = 0;
  panelWidth = 0;
  panelMaxHeight = 320;

  private listening = false;
  private onChange: (value: unknown) => void = () => {};
  private onTouched: () => void = () => {};
  private readonly onViewportChange = () => {
    if (this.open) {
      this.positionPanel();
    }
  };

  ngOnDestroy(): void {
    this.listenToViewport(false);
  }

  writeValue(value: unknown): void {
    this.value = value;
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.close();
    }
  }

  get selectedLabel(): string {
    const match = this.flatOptions.find((option) =>
      this.sameValue(option.value, this.value)
    );
    return match?.label || this.placeholder;
  }

  get flatOptions(): DropdownValue[] {
    if (this.groups?.length) {
      return this.groups.flatMap((group) => group.items ?? []);
    }
    return this.options ?? [];
  }

  toggle(): void {
    if (this.disabled) {
      return;
    }
    if (this.open) {
      this.close();
      return;
    }
    this.positionPanel();
    this.open = true;
    this.listenToViewport(true);
  }

  close(): void {
    if (!this.open) {
      return;
    }
    this.open = false;
    this.listenToViewport(false);
    this.onTouched();
  }

  select(option: DropdownValue): void {
    this.value = option.value;
    this.onChange(option.value);
    this.close();
  }

  isSelected(option: DropdownValue): boolean {
    return this.sameValue(option.value, this.value);
  }

  @HostListener("document:keydown.escape")
  onEscape(): void {
    this.close();
  }

  private positionPanel(): void {
    const rect = this.trigger?.nativeElement.getBoundingClientRect();
    if (!rect) {
      return;
    }

    const gap = 4;
    const spaceBelow = window.innerHeight - rect.bottom - gap - 8;
    const spaceAbove = rect.top - gap - 8;
    const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(120, openUp ? spaceAbove : spaceBelow);

    this.panelMaxHeight = maxHeight;
    this.panelLeft = rect.left;
    this.panelWidth = rect.width;
    if (openUp) {
      this.panelTop = null;
      this.panelBottom = window.innerHeight - rect.top + gap;
    } else {
      this.panelTop = rect.bottom + gap;
      this.panelBottom = null;
    }
  }

  private listenToViewport(active: boolean): void {
    if (active && !this.listening) {
      document.addEventListener("scroll", this.onViewportChange, true);
      window.addEventListener("resize", this.onViewportChange);
      this.listening = true;
    } else if (!active && this.listening) {
      document.removeEventListener("scroll", this.onViewportChange, true);
      window.removeEventListener("resize", this.onViewportChange);
      this.listening = false;
    }
  }

  private sameValue(left: unknown, right: unknown): boolean {
    return left === right || (left != null && right != null && `${left}` === `${right}`);
  }
}
